import React, { useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import FontFamily from "@tiptap/extension-font-family";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Divider from "@material-ui/core/Divider";

import FormatBoldIcon from "@material-ui/icons/FormatBold";
import FormatItalicIcon from "@material-ui/icons/FormatItalic";
import FormatUnderlinedIcon from "@material-ui/icons/FormatUnderlined";
import FormatAlignLeftIcon from "@material-ui/icons/FormatAlignLeft";
import FormatAlignCenterIcon from "@material-ui/icons/FormatAlignCenter";
import FormatAlignRightIcon from "@material-ui/icons/FormatAlignRight";
import FormatAlignJustifyIcon from "@material-ui/icons/FormatAlignJustify";
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted";
import FormatListNumberedIcon from "@material-ui/icons/FormatListNumbered";
import TableChartIcon from "@material-ui/icons/TableChart";
import DeleteSweepIcon from "@material-ui/icons/DeleteSweep";
import SettingsIcon from "@material-ui/icons/Settings";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import FontSize from "./FontSize";
import TemplateVariableNode from "./TemplateVariableNode";
import VariableMenu from "./VariableMenu";
import PageConfigPanel from "./PageConfigPanel";
import { FONT_OPTIONS, FONT_SIZES, DEFAULT_PAGE_CONFIG } from "./constants";

const useStyles = makeStyles((theme) => ({
  toolbar: {
    position: "sticky",
    top: 0,
    zIndex: 5,
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 4,
    padding: theme.spacing(0.5, 1),
    marginBottom: theme.spacing(2),
  },
  toolbarDivider: {
    height: 24,
    margin: theme.spacing(0, 0.5),
  },
  fontSelect: {
    minWidth: 140,
    fontSize: 13,
  },
  sizeSelect: {
    minWidth: 56,
    fontSize: 13,
  },
  deskArea: {
    background: theme.palette.type === "dark" ? "#333" : "#e0e0e0",
    padding: theme.spacing(3, 1),
    overflow: "auto",
    flex: 1,
  },
  sheet: {
    width: "21cm",
    minHeight: "29.7cm",
    margin: "0 auto",
    background: "#fff",
    color: "#000",
    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
    boxSizing: "border-box",
    "& .ProseMirror": {
      outline: "none",
      minHeight: "24cm",
      lineHeight: 1.5,
      "& p": { margin: "0 0 0.6em 0" },
      "& table": {
        borderCollapse: "collapse",
        width: "100%",
        "& td, & th": {
          border: "1px solid #444",
          padding: "4px 8px",
          position: "relative",
        },
        "& .selectedCell": { background: "#e3f2fd" },
      },
      // O chip herda fonte/tamanho do texto ao redor — só muda o fundo,
      // para não divergir do resultado impresso
      "& span.tpl-var": {
        background: "#e3f2fd",
        border: "1px solid #90caf9",
        borderRadius: 4,
        padding: "0 2px",
        color: "inherit",
        fontFamily: "inherit",
        fontSize: "inherit",
        whiteSpace: "nowrap",
        cursor: "default",
      },
      "& span.tpl-var.ProseMirror-selectednode": {
        background: "#1565c0",
        color: "#fff",
      },
    },
  },
  activeButton: {
    background: theme.palette.action.selected,
  },
}));

const letterSize = { width: "21.6cm", minHeight: "27.9cm" };

const DocumentTemplateEditor = ({
  initialContent,
  pageConfig,
  onPageConfigChange,
  onEditorReady,
}) => {
  const classes = useStyles();
  const [variableCatalog, setVariableCatalog] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  // força re-render da toolbar quando a seleção muda
  const [, setRenderTick] = useState(0);

  const cfg = { ...DEFAULT_PAGE_CONFIG, ...pageConfig };
  const margins = { ...DEFAULT_PAGE_CONFIG.margins, ...(cfg.margins || {}) };

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      FontSize,
      FontFamily.configure({ types: ["textStyle"] }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      TemplateVariableNode,
    ],
    content: initialContent || "<p></p>",
    onSelectionUpdate: () => setRenderTick((t) => t + 1),
    onTransaction: () => setRenderTick((t) => t + 1),
  });

  useEffect(() => {
    if (editor && onEditorReady) {
      onEditorReady(editor);
    }
  }, [editor, onEditorReady]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/document-templates/variables");
        setVariableCatalog(data);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  if (!editor) return null;

  const currentFont =
    editor.getAttributes("textStyle").fontFamily || cfg.fontFamily;
  const currentSizeRaw = editor.getAttributes("textStyle").fontSize;
  const currentSize = currentSizeRaw
    ? parseInt(currentSizeRaw, 10)
    : cfg.fontSize;

  const handleInsertVariable = (variable) => {
    // Herda a formatação ativa no cursor (fonte, tamanho, negrito...)
    // para o chip não "quebrar" o estilo do texto ao redor
    const { state } = editor;
    const activeMarks = (
      state.storedMarks || state.selection.$from.marks() || []
    ).map((mark) => ({ type: mark.type.name, attrs: mark.attrs }));

    editor
      .chain()
      .focus()
      .insertContent([
        {
          type: "templateVariable",
          attrs: { name: variable.key, label: variable.label },
          marks: activeMarks,
        },
        { type: "text", text: " ", marks: activeMarks },
      ])
      .run();
  };

  const toggleButton = (label, icon, isActive, onClick) => (
    <Tooltip title={label} key={label}>
      <IconButton
        size="small"
        className={isActive ? classes.activeButton : undefined}
        onClick={onClick}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );

  const sheetSize =
    cfg.paperSize === "Letter"
      ? letterSize
      : { width: "21cm", minHeight: "29.7cm" };

  return (
    <>
      <Paper variant="outlined" className={classes.toolbar}>
        <Select
          value={currentFont}
          className={classes.fontSelect}
          onChange={(e) =>
            editor.chain().focus().setFontFamily(e.target.value).run()
          }
        >
          {FONT_OPTIONS.map((font) => (
            <MenuItem key={font} value={font} style={{ fontFamily: font }}>
              {font}
            </MenuItem>
          ))}
        </Select>
        <Select
          value={currentSize}
          className={classes.sizeSelect}
          onChange={(e) =>
            editor.chain().focus().setFontSize(`${e.target.value}pt`).run()
          }
        >
          {FONT_SIZES.map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </Select>

        <Divider orientation="vertical" className={classes.toolbarDivider} />

        {toggleButton(
          i18n.t("documentTemplates.toolbar.bold"),
          <FormatBoldIcon fontSize="small" />,
          editor.isActive("bold"),
          () => editor.chain().focus().toggleBold().run()
        )}
        {toggleButton(
          i18n.t("documentTemplates.toolbar.italic"),
          <FormatItalicIcon fontSize="small" />,
          editor.isActive("italic"),
          () => editor.chain().focus().toggleItalic().run()
        )}
        {toggleButton(
          i18n.t("documentTemplates.toolbar.underline"),
          <FormatUnderlinedIcon fontSize="small" />,
          editor.isActive("underline"),
          () => editor.chain().focus().toggleUnderline().run()
        )}

        <Divider orientation="vertical" className={classes.toolbarDivider} />

        {toggleButton(
          i18n.t("documentTemplates.toolbar.alignLeft"),
          <FormatAlignLeftIcon fontSize="small" />,
          editor.isActive({ textAlign: "left" }),
          () => editor.chain().focus().setTextAlign("left").run()
        )}
        {toggleButton(
          i18n.t("documentTemplates.toolbar.alignCenter"),
          <FormatAlignCenterIcon fontSize="small" />,
          editor.isActive({ textAlign: "center" }),
          () => editor.chain().focus().setTextAlign("center").run()
        )}
        {toggleButton(
          i18n.t("documentTemplates.toolbar.alignRight"),
          <FormatAlignRightIcon fontSize="small" />,
          editor.isActive({ textAlign: "right" }),
          () => editor.chain().focus().setTextAlign("right").run()
        )}
        {toggleButton(
          i18n.t("documentTemplates.toolbar.justify"),
          <FormatAlignJustifyIcon fontSize="small" />,
          editor.isActive({ textAlign: "justify" }),
          () => editor.chain().focus().setTextAlign("justify").run()
        )}

        <Divider orientation="vertical" className={classes.toolbarDivider} />

        {toggleButton(
          i18n.t("documentTemplates.toolbar.bulletList"),
          <FormatListBulletedIcon fontSize="small" />,
          editor.isActive("bulletList"),
          () => editor.chain().focus().toggleBulletList().run()
        )}
        {toggleButton(
          i18n.t("documentTemplates.toolbar.orderedList"),
          <FormatListNumberedIcon fontSize="small" />,
          editor.isActive("orderedList"),
          () => editor.chain().focus().toggleOrderedList().run()
        )}

        <Divider orientation="vertical" className={classes.toolbarDivider} />

        {toggleButton(
          i18n.t("documentTemplates.toolbar.insertTable"),
          <TableChartIcon fontSize="small" />,
          editor.isActive("table"),
          () =>
            editor
              .chain()
              .focus()
              .insertTable({ rows: 2, cols: 2, withHeaderRow: true })
              .run()
        )}
        {editor.isActive("table") &&
          toggleButton(
            i18n.t("documentTemplates.toolbar.removeTable"),
            <DeleteSweepIcon fontSize="small" />,
            false,
            () => editor.chain().focus().deleteTable().run()
          )}

        <Divider orientation="vertical" className={classes.toolbarDivider} />

        <VariableMenu
          catalog={variableCatalog}
          onInsert={handleInsertVariable}
        />

        <Tooltip title={i18n.t("documentTemplates.buttons.pageConfig")}>
          <IconButton size="small" onClick={() => setConfigOpen(true)}>
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>

      <div className={classes.deskArea}>
        <div
          className={classes.sheet}
          style={{
            width: sheetSize.width,
            minHeight: sheetSize.minHeight,
            paddingTop: `${margins.top}cm`,
            paddingRight: `${margins.right}cm`,
            paddingBottom: `${margins.bottom}cm`,
            paddingLeft: `${margins.left}cm`,
            fontFamily: cfg.fontFamily,
            fontSize: `${cfg.fontSize}pt`,
          }}
          onClick={() => editor.chain().focus().run()}
        >
          <EditorContent editor={editor} />
        </div>
      </div>

      <PageConfigPanel
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        pageConfig={cfg}
        onChange={onPageConfigChange}
      />
    </>
  );
};

export default DocumentTemplateEditor;
